import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { PriceDisplayWithTerm } from 'pricing-pages-ui-components/price/PriceDisplayWithTerm';
import { getButtonVariantProps, getTrellisButtonVariantProps } from 'pricing-pages-ui-components/utils/buttonVariantUtils';
import { MERCH_CARD } from 'ui-addon-upgrades/_core/common/constants/PurchaseMotionLocations';
import SingleProductCTA from '../../../features/purchaseMotions/components/SingleProductCTA';
import { TalkToSalesFallback } from '../../../features/purchaseMotions/components/TalkToSalesFallback';
import { useBundledProductPurchaseMotions } from '../../../features/purchaseMotions/hooks/useBundledProductPurchaseMotions';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { usePurchaseableProductsForTerm } from '../../../hooks/useBulkPurchaseOptions';
import { useCurrencyCodeUnitToDisplay, useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { useAppSelector } from '../../../reducers';
import { productSelector } from '../../../selectors/ProductsSelectors';
import { useQueryParamTerm } from '../../../utils/urls/appQueryParamUtilsLegacy';
import { ErrorBoundary } from '../../../v2/components/error-boundary/ErrorBoundary';
import { SMART_CRM_FEATURE_TABLE_HEADER } from '../../../v2/constants/tracking/TrackingSources';
import { useHasApiNameInProductInventory } from '../../../v2/hooks/inventory/useProductInventoryLookup';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import { getI18nKeyOverrideForProductCard } from '../../merch-card/I18nUtils';
import FeatureTableColTag from '../FeatureTableColTag';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SmartCrmFeatureTableHeaderCell = ({
  apiName,
  screen,
  badgeName
}) => {
  var _getI18nKeyOverrideFo;
  const isAppReady = useGlobalLoading();
  const product = useAppSelector(state => {
    if (!isAppReady) return null;
    return productSelector(state, {
      apiName
    });
  });
  const skuIdToUse = product === null || product === void 0 ? void 0 : product.currentSkuId;
  const {
    loading: inventoryLoading,
    hasProductInInventory: isCurrentPlanLevel,
    error: inventoryError
  } = useHasApiNameInProductInventory({
    apiName
  });
  const purchaseMotions = useBundledProductPurchaseMotions({
    skuId: skuIdToUse
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const {
    purchaseOptionsForTerm,
    loading: purchaseOptionsForTermLoading
  } = usePurchaseableProductsForTerm({
    queryParamTerm,
    apiName: product === null || product === void 0 ? void 0 : product.apiName
  });
  const loading = purchaseOptionsForTermLoading || inventoryLoading || !isAppReady;
  const currencyCode = useDisplayedCurrencyCode();
  const currencyCodeUnit = useCurrencyCodeUnitToDisplay();
  const productLevel = product === null || product === void 0 ? void 0 : product.productLevel;
  if (!productLevel || !purchaseOptionsForTerm && !purchaseOptionsForTermLoading || inventoryError) {
    return null;
  }
  const ctaVariantProps = getButtonVariantProps({
    variant: 'table-header'
  });
  const trellisCtaVariantProps = getTrellisButtonVariantProps({
    variant: 'table-header',
    visible: isCurrentPlanLevel // render space as if button is visible to prevent layout shift
  });
  const title = (_getI18nKeyOverrideFo = getI18nKeyOverrideForProductCard(apiName)) === null || _getI18nKeyOverrideFo === void 0 ? void 0 : _getI18nKeyOverrideFo.Title;
  return /*#__PURE__*/_jsxs(Flex, {
    paddingInline: "0",
    flexDirection: "column",
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(FeatureTableColTag, {
      apiName: apiName,
      badgeName: badgeName
    }), title && /*#__PURE__*/_jsx(Text, {
      as: "strong",
      typography: "heading-50",
      children: title
    }), /*#__PURE__*/_jsx(View, {
      marginBlock: "100",
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: loading,
        heights: [20],
        widths: [140],
        children: purchaseOptionsForTerm && /*#__PURE__*/_jsx(PriceDisplayWithTerm, {
          pricingInfo: purchaseOptionsForTerm,
          seleniumId: apiName || 'loading',
          currencyCode: currencyCode,
          currencyCodeUnit: currencyCodeUnit
        })
      })
    }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: !isAppReady,
      heights: [40],
      widths: [140],
      children: /*#__PURE__*/_jsx(View, Object.assign({}, trellisCtaVariantProps, {
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
            apiName: apiName,
            trackingSource: SMART_CRM_FEATURE_TABLE_HEADER,
            screen: screen
          }),
          children: /*#__PURE__*/_jsx(SingleProductCTA, Object.assign({
            purchaseMotion: purchaseMotions[0],
            apiName: apiName,
            screen: screen,
            trackingSource: SMART_CRM_FEATURE_TABLE_HEADER,
            purchaseMotionLocation: MERCH_CARD,
            skipInterstitialPage: true
          }, ctaVariantProps), String(purchaseMotions[0]))
        })
      }))
    })]
  });
};
export default SmartCrmFeatureTableHeaderCell;